export function toFiniteNumber(value, fallback = 0) {
  if (value === '' || value == null) {
    return fallback;
  }
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
}

export function formatSignedWeight(value) {
  if (value === '' || value == null) {
    return '—';
  }
  const number = Number(value);
  if (!Number.isFinite(number)) {
    return '—';
  }
  const formatted = number.toFixed(2);
  if (number > 0) {
    return `+${formatted}`;
  }
  return formatted;
}

export function clampPercent(value) {
  const number = Number(value);
  if (!Number.isFinite(number)) {
    return 0;
  }
  return Math.min(100, Math.max(0, number));
}

export function parseWeight(value) {
  if (value === '' || value == null) {
    return null;
  }
  const number = Number(value);
  if (!Number.isFinite(number)) {
    return null;
  }
  return Math.round(number * 100) / 100;
}

export function isValidWeight(value) {
  const number = parseWeight(value);
  return number != null && number >= -100 && number <= 100;
}

export function localizedName(entity, locale) {
  if (!entity) {
    return '';
  }
  if (locale === 'ar') {
    return entity.name_ar || entity.title_ar || entity.text_ar || entity.name || entity.title || entity.text || '';
  }
  return entity.name_en || entity.title_en || entity.text_en || entity.name || entity.title || entity.text || '';
}

export function formatFileSize(bytes, locale = 'en') {
  const size = Number(bytes);
  if (!Number.isFinite(size) || size < 0) {
    return '—';
  }
  if (size < 1024) {
    return `${size} B`;
  }
  const units = ['KB', 'MB', 'GB'];
  let value = size / 1024;
  let unitIndex = 0;
  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }
  const formatted = new Intl.NumberFormat(locale === 'ar' ? 'ar' : 'en', {
    maximumFractionDigits: value >= 10 ? 0 : 1,
  }).format(value);
  return `${formatted} ${units[unitIndex]}`;
}

export function formatDateTime(value, locale) {
  if (!value) {
    return '—';
  }
  try {
    return new Intl.DateTimeFormat(locale === 'ar' ? 'ar' : 'en', {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(new Date(value));
  } catch {
    return String(value);
  }
}

export function normalizeShortCode(value) {
  return String(value || '').replace(/[^A-Za-z]/g, '').slice(0, 4).toUpperCase();
}

export function isDeletedUser(user) {
  if (!user) {
    return true;
  }
  const name = String(user.name || '').trim();
  const phone = String(user.phone || user.phone_number || '');
  if (user.is_archived || user.deleted_at) {
    return true;
  }
  if (!name || /^deleted user$/i.test(name)) {
    return true;
  }
  return phone.startsWith('deleted_');
}

export function formatUserLabel(user, t) {
  if (isDeletedUser(user)) {
    return t('users.deleted');
  }
  return user.name;
}

export function formatUserContact(user) {
  if (isDeletedUser(user)) {
    return null;
  }
  const phone = user.phone || user.phone_number;
  if (!phone || String(phone).startsWith('deleted_')) {
    return null;
  }
  return phone;
}

export function safeHttpUrl(value) {
  if (!value || typeof value !== 'string') {
    return null;
  }
  try {
    const parsed = new URL(value);
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
      return null;
    }
    return parsed.toString();
  } catch {
    return null;
  }
}

export function resolveStorageUrl(path, apiBaseUrl) {
  if (!path) {
    return null;
  }
  const direct = safeHttpUrl(path);
  if (direct) {
    return direct;
  }
  if (!apiBaseUrl) {
    return null;
  }
  const origin = String(apiBaseUrl).replace(/\/api\/v1\/?$/, '');
  return safeHttpUrl(`${origin}/storage/${String(path).replace(/^\/+/, '')}`);
}

export function asList(data) {
  if (Array.isArray(data)) {
    return data;
  }
  if (Array.isArray(data?.items)) {
    return data.items;
  }
  if (Array.isArray(data?.data)) {
    return data.data;
  }
  return [];
}

export function debounce(fn, wait = 300) {
  let timer;
  const debounced = (...args) => {
    window.clearTimeout(timer);
    timer = window.setTimeout(() => fn(...args), wait);
  };
  debounced.cancel = () => window.clearTimeout(timer);
  return debounced;
}

export function serializeFilters(filters = {}) {
  const params = {};
  Object.entries(filters).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') {
      return;
    }
    params[key] = value;
  });
  return params;
}

export function sortResultPatterns(patterns = []) {
  return [...patterns].sort((left, right) => {
    const rankL = Number(left.rank);
    const rankR = Number(right.rank);
    if (Number.isFinite(rankL) && Number.isFinite(rankR) && rankL !== rankR) {
      return rankL - rankR;
    }
    const scoreL = Number(left.percentage);
    const scoreR = Number(right.percentage);
    return (Number.isFinite(scoreR) ? scoreR : 0) - (Number.isFinite(scoreL) ? scoreL : 0);
  });
}

export function weightRowStatus(answer) {
  if (answer?.configuration_status) {
    return answer.configuration_status;
  }
  if (answer?.weight == null) {
    return 'pending';
  }
  return 'configured';
}

export function mapIndexedWeightErrors(errors = {}, weights = []) {
  const mapped = {};
  Object.entries(errors || {}).forEach(([path, messages]) => {
    const list = Array.isArray(messages) ? messages.filter(Boolean) : [messages].filter(Boolean);
    if (list.length === 0) {
      return;
    }
    mapped[path] = list.map(String);
    const match = path.match(/^weights\.(\d+)\.weight$/);
    if (match) {
      const row = weights[Number(match[1])];
      if (row?.assessment_answer_id != null) {
        mapped[`weights.${row.assessment_answer_id}.weight`] = list.map(String);
      }
    }
  });
  return mapped;
}

export function uniqueById(items = [], key = 'id') {
  const seen = new Set();
  return items.filter((item) => {
    const id = item?.[key] ?? item?.id;
    if (id == null) {
      return true;
    }
    if (seen.has(Number(id))) {
      return false;
    }
    seen.add(Number(id));
    return true;
  });
}

export function isValidQuestionMove(previous, next) {
  const prevIds = previous.flatMap((section) => (section.questions || []).map((question) => question.id)).sort((a, b) => a - b);
  const nextIds = next.flatMap((section) => (section.questions || []).map((question) => question.id)).sort((a, b) => a - b);
  if (prevIds.length !== nextIds.length) {
    return false;
  }
  if (new Set(nextIds).size !== nextIds.length) {
    return false;
  }
  return prevIds.every((id, index) => id === nextIds[index]);
}

export function readPublishFlag(source) {
  if (!source || typeof source !== 'object') {
    return null;
  }
  if (typeof source.can_publish === 'boolean') {
    return source.can_publish;
  }
  if (source.version_readiness && typeof source.version_readiness.can_publish === 'boolean') {
    return source.version_readiness.can_publish;
  }
  return null;
}

export function canPublishVersion(version, _coverage) {
  if (!version || version.status === 'published' || version.status === 'archived') {
    return false;
  }
  return readPublishFlag(version) === true;
}

export function versionBlockingReasons(version) {
  if (!version || typeof version !== 'object') {
    return [];
  }
  const reasons = version.blocking_reasons
    || version.version_readiness?.blocking_reasons
    || [];
  return Array.isArray(reasons) ? reasons : [];
}

export function questionHasPendingWeights(question) {
  return (question?.answers || []).some((answer) =>
    (answer.weights || []).some((weight) => weight.configuration_status && weight.configuration_status !== 'configured'),
  );
}

export function buildQuestionReorderPayload(sections) {
  return {
    questions: sections.flatMap((section) =>
      (section.questions || []).map((question) => ({
        id: question.id,
        assessment_section_id: section.id,
      })),
    ),
  };
}

export function moveSection(sections, activeId, overId) {
  const ids = sections.map((section) => `section-${section.id}`);
  const oldIndex = ids.indexOf(String(activeId));
  const newIndex = ids.indexOf(String(overId));
  if (oldIndex < 0 || newIndex < 0 || oldIndex === newIndex) {
    return sections;
  }
  const next = [...sections];
  const [item] = next.splice(oldIndex, 1);
  next.splice(newIndex, 0, item);
  return next;
}

export function applyBoardDragEnd(board, activeId, overId, locked = false) {
  if (locked || !activeId || !overId || activeId === overId) {
    return null;
  }
  if (activeId.startsWith('section-') && overId.startsWith('section-')) {
    const next = moveSection(board, activeId, overId);
    if (next === board) {
      return null;
    }
    return {
      type: 'sections',
      next,
      sectionIds: next.map((section) => section.id),
    };
  }
  if (activeId.startsWith('question-')) {
    const questionId = Number(activeId.replace('question-', ''));
    const overQuestion = overId.startsWith('question-') ? Number(overId.replace('question-', '')) : null;
    const overSectionId = overId.startsWith('section-')
      ? Number(overId.replace('section-', ''))
      : board.find((section) => (section.questions || []).some((question) => question.id === overQuestion))?.id;
    const next = moveQuestion(board, questionId, overSectionId, overQuestion);
    if (next === board || !isValidQuestionMove(board, next)) {
      return null;
    }
    return {
      type: 'questions',
      next,
      questions: buildQuestionReorderPayload(next).questions,
    };
  }
  return null;
}

export function moveQuestion(sections, activeId, overSectionId, overQuestionId) {
  const cloned = sections.map((section) => ({
    ...section,
    questions: [...(section.questions || [])],
  }));
  const source = cloned.find((section) => section.questions.some((question) => question.id === activeId));
  const target = cloned.find((section) => section.id === overSectionId);
  if (!source || !target) {
    return sections;
  }
  const fromIndex = source.questions.findIndex((question) => question.id === activeId);
  if (fromIndex < 0) {
    return sections;
  }

  if (source.id === target.id) {
    const ids = source.questions.map((question) => question.id);
    const newIndex = overQuestionId == null
      ? ids.length - 1
      : ids.indexOf(overQuestionId);
    if (newIndex < 0 || newIndex === fromIndex) {
      return sections;
    }
    const [moved] = source.questions.splice(fromIndex, 1);
    source.questions.splice(newIndex, 0, {
      ...moved,
      assessment_section_id: source.id,
    });
    return cloned;
  }

  const [moved] = source.questions.splice(fromIndex, 1);
  if (!moved) {
    return sections;
  }
  const toIndex = overQuestionId
    ? target.questions.findIndex((question) => question.id === overQuestionId)
    : target.questions.length;
  target.questions.splice(toIndex < 0 ? target.questions.length : toIndex, 0, {
    ...moved,
    assessment_section_id: target.id,
  });
  return cloned;
}
